/**
 * Iter C.2.1.CAP — Capability picker (authoring control).
 *
 * Multi-select chip input used in the catalogue admin form to author
 * capability codes for an offering. Suggests the curated soft-list from
 * the server but also accepts operator-supplied free-text codes
 * (normalized server-side on save).
 *
 * Per Rahul's 2026-02 direction: lightweight, business-focused. No
 * governance complexity. Operators see iconified suggestion chips and
 * a small "+ Add custom" affordance.
 *
 * Usage:
 *   <CapabilityPicker value={form.capabilities} onChange={(arr) => ...} />
 */
import React, { useEffect, useState } from "react";
import { X, Plus } from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { CapabilityBadges } from "./CapabilityBadges";

const normalise = (code) =>
    String(code || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

export function CapabilityPicker({ value, onChange }) {
    const [suggestions, setSuggestions] = useState([]);
    const [customInput, setCustomInput] = useState("");

    useEffect(() => {
        let alive = true;
        api.get("/billing/catalogue/capability-suggestions")
            .then((r) => { if (alive) setSuggestions(r.data.items || []); })
            .catch(() => { /* graceful — picker degrades to custom-only */ });
        return () => { alive = false; };
    }, []);

    const selected = Array.isArray(value) ? value : [];
    const has = (code) => selected.includes(code);

    const toggle = (code) => {
        const n = normalise(code);
        if (!n) return;
        if (has(n)) onChange(selected.filter((c) => c !== n));
        else onChange([...selected, n]);
    };

    const addCustom = () => {
        const n = normalise(customInput);
        if (n && !has(n)) onChange([...selected, n]);
        setCustomInput("");
    };

    return (
        <div className="space-y-2" data-testid="capability-picker">
            <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-600">
                    Capabilities <span className="font-normal text-slate-400">(optional · soft list)</span>
                </span>
                {selected.length > 0 && (
                    <button
                        type="button"
                        onClick={() => onChange([])}
                        className="text-[10px] text-slate-400 hover:text-rose-500"
                        data-testid="capability-clear-all"
                    >
                        Clear all
                    </button>
                )}
            </div>

            {/* Suggestion chips — click to toggle. */}
            <div className="flex flex-wrap gap-1.5" data-testid="capability-suggestions">
                {suggestions.map((s) => (
                    <button
                        type="button"
                        key={s.code}
                        onClick={() => toggle(s.code)}
                        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-[11px] transition-colors ${has(s.code)
                            ? "bg-emerald-100 text-emerald-900 border-emerald-300"
                            : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
                        data-testid={`capability-suggest-${s.code}`}
                    >
                        {has(s.code) && <X size={11} weight="bold" />}
                        {!has(s.code) && <Plus size={11} weight="bold" />}
                        {s.label}
                    </button>
                ))}
            </div>

            {/* Custom-code input — free text for ad-hoc capabilities. */}
            <div className="flex items-center gap-2">
                <Input
                    value={customInput}
                    onChange={(e) => setCustomInput(e.target.value.slice(0, 40))}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
                    placeholder="Add custom capability code (e.g. crypto, family_office)"
                    className="h-8 text-sm"
                    data-testid="capability-custom-input"
                />
                <Button
                    type="button" size="sm" variant="outline"
                    onClick={addCustom}
                    disabled={!normalise(customInput)}
                    data-testid="capability-custom-add"
                >
                    Add
                </Button>
            </div>

            {/* Preview of what is currently selected. */}
            {selected.length > 0 && (
                <div className="pt-1">
                    <CapabilityBadges capabilities={selected} size="sm" />
                </div>
            )}
        </div>
    );
}

export default CapabilityPicker;
